import {
  BROKER_SYNC_ISSUE_KIND_LABELS, BROKER_SYNC_PROVIDER, BROKER_SYNC_PROVIDER_LABELS,
  BROKER_SYNC_REVIEW_ACTION_LABELS, BROKER_SYNC_REVIEW_ACTION_OBJ,
  BROKER_SYNC_REVIEW_QUALITY_LABELS, PORTFOLIO_DIVIDEND_REVIEW_REASON_LABELS, isRecord,
  type BrokerSyncIssue, type BrokerSyncIssueDetails, type BrokerSyncReviewMoney,
  type BrokerSyncAccountStatus, type BrokerSyncReviewReason, type TBrokerSyncProvider,
} from "@signals/shared";
import { Button } from "../_common";
import { formatDateTime, localeKey, t } from "./broker-sync-panel-format.util";
import { redactKnownSecrets, sanitizePayload } from "./broker-sync-panel-error.util";

type NormalizedIssueDetails = Omit<BrokerSyncIssueDetails, "provider"> & { provider: TBrokerSyncProvider | null };

export function brokerReviewReasonLabel(reason: BrokerSyncReviewReason): string {
  const labels: Record<string, Record<string, string>> = {
    ...BROKER_SYNC_ISSUE_KIND_LABELS, ...PORTFOLIO_DIVIDEND_REVIEW_REASON_LABELS, ...BROKER_SYNC_REVIEW_QUALITY_LABELS,
  };
  return labels[reason]?.[localeKey()] ?? "Запись требует проверки";
}

export function brokerReviewDate(value: string | null | undefined): string {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return "Дата операции неизвестна";
  const [year, month, day] = value.split("-");
  return `${day}.${month}.${year}`;
}

export function brokerReviewMoney(value: BrokerSyncReviewMoney | null): string {
  return value && Number.isFinite(value.amount)
    ? `${new Intl.NumberFormat(localeKey(), { maximumFractionDigits: 8 }).format(value.amount)} ${value.currency}`
    : "Неизвестно";
}

/** Every string is masked before React receives it, including strings inside a closed details element. */
export function safeBrokerReviewValue(value: unknown, secrets: readonly string[]): unknown {
  const sanitized = sanitizePayload(value);
  const mask = (item: unknown): unknown => {
    if (typeof item === "string") return redactKnownSecrets(item, secrets);
    if (Array.isArray(item)) return item.map(mask);
    if (!isRecord(item)) return item;
    return Object.fromEntries(Object.entries(item).map(([key, nested]) => [redactKnownSecrets(key, secrets), mask(nested)]));
  };
  return mask(sanitized);
}

/** Saved legacy payloads have no display proof: only their canonical operation is considered. */
export function normalizeBrokerSyncIssueDetails(issue: BrokerSyncIssue, account?: BrokerSyncAccountStatus): NormalizedIssueDetails {
  if (issue.details) return issue.details;
  const operation = isRecord(issue.payload["operation"]) ? issue.payload["operation"] : null;
  const provider = account?.provider ?? BROKER_SYNC_PROVIDER.find((candidate) => candidate === operation?.["provider"]) ?? null;
  const date = operation?.["operationDate"];
  const amount = operation?.["amount"];
  const currency = operation?.["currency"];
  const quantity = operation?.["quantity"];
  const decimal = (value: unknown): value is string => typeof value === "string" && /^-?(?:0|[1-9]\d*)(?:\.\d+)?$/.test(value);
  const money = decimal(amount) && Number.isFinite(Number(amount)) && typeof currency === "string" && currency.trim()
    ? { amount: Number(amount), currency } : null;
  return {
    provider, accountName: account?.accountName ?? "Счёт не указан",
    operationDate: typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : null,
    instrument: { name: null, ticker: null, isin: null },
    quantity: decimal(quantity) ? quantity : null, money,
    reason: issue.kind, reasonText: brokerReviewReasonLabel(issue.kind),
    financialImpact: "Влияние этой записи на деньги и оценку не подтверждено. Сохранённые операции остаются без изменений.",
    nextStep: "Сопоставьте запись с подтверждённой выпиской брокера.",
    alreadyAccounted: { status: "unknown", text: "Не установлено, была ли эта запись уже учтена." },
    missingEvidence: ["Подтверждённые сведения об операции и её связи с учётом."],
    actions: [{ kind: BROKER_SYNC_REVIEW_ACTION_OBJ.STATEMENT_REQUIRED, enabled: true, reason: null }],
  };
}

export interface BrokerSyncIssueDetailsCardProps {
  issue: BrokerSyncIssue;
  account?: BrokerSyncAccountStatus | undefined;
  managementCode?: string | undefined;
  secretValues?: readonly string[] | undefined;
  onPrepare?: ((issue: BrokerSyncIssue) => void) | undefined;
}

export function BrokerSyncIssueDetailsCard({ issue, account, managementCode = "", secretValues = [], onPrepare }: BrokerSyncIssueDetailsCardProps) {
  const secrets = [managementCode, ...secretValues];
  const safe = (text: string) => redactKnownSecrets(text, secrets);
  const details = normalizeBrokerSyncIssueDetails(issue, account);
  const instrument = [details.instrument.name, details.instrument.ticker, details.instrument.isin].filter(Boolean).join(" · ");
  const missing = details.missingEvidence.length > 0 ? details.missingEvidence : ["Полный состав недостающих сведений пока не установлен."];
  return (
    <article className="min-w-0 space-y-3 rounded-md border p-3 [overflow-wrap:anywhere]">
      <h4 className="font-medium">{brokerReviewReasonLabel(details.reason)}</h4>
      <p className="text-sm text-muted-foreground">
        {details.provider ? BROKER_SYNC_PROVIDER_LABELS[details.provider][localeKey()] : "Брокер не указан"} · {safe(details.accountName)}
      </p>
      <dl className="grid min-w-0 gap-2 text-sm sm:grid-cols-2">
        <div><dt className="text-muted-foreground">Дата операции</dt><dd>{brokerReviewDate(details.operationDate)}</dd></div>
        <div><dt className="text-muted-foreground">Инструмент</dt><dd>{instrument ? safe(instrument) : "Инструмент не подтверждён"}</dd></div>
        <div><dt className="text-muted-foreground">Количество</dt><dd>{details.quantity === null ? "Неизвестно" : safe(details.quantity)}</dd></div>
        <div><dt className="text-muted-foreground">Сумма операции</dt><dd>{safe(brokerReviewMoney(details.money))}</dd></div>
      </dl>
      <div className="space-y-2 text-sm">
        <p><span className="font-medium">Причина: </span>{safe(details.reasonText)}</p>
        <p><span className="font-medium">Что уже учтено: </span>{safe(details.alreadyAccounted.text)}</p>
        <p><span className="font-medium">Влияние на деньги и оценку: </span>{safe(details.financialImpact)}</p>
        <p><span className="font-medium">Следующий шаг: </span>{safe(details.nextStep)}</p>
      </div>
      <details className="min-w-0 text-sm">
        <summary className="min-h-9 cursor-pointer py-2 font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">Чего не хватает</summary>
        <ul className="list-inside list-disc space-y-1">
          {missing.map((item, index) => <li key={index}>{safe(item)}</li>)}
        </ul>
      </details>
      <div className="flex min-w-0 flex-wrap gap-2">
        {details.actions.filter((action) => action.kind !== BROKER_SYNC_REVIEW_ACTION_OBJ.STATEMENT_REQUIRED).map((action) => (
          <div key={action.kind} className="min-w-0 max-w-full space-y-1">
            <Button type="button" variant="outline" size="sm" className="h-auto min-h-9 max-w-full whitespace-normal text-left"
              disabled={!action.enabled || !onPrepare} onClick={() => onPrepare?.(issue)}>
              {BROKER_SYNC_REVIEW_ACTION_LABELS[action.kind][localeKey()]}
            </Button>
            {action.reason && <p className="text-xs text-muted-foreground">{safe(action.reason)}</p>}
          </div>
        ))}
      </div>
      <details className="min-w-0">
        <summary className="min-h-9 cursor-pointer py-2 text-sm text-muted-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">{t("portfolio.brokerSync.issueDetails")}</summary>
        <div className="min-w-0 space-y-2 text-xs text-muted-foreground">
          <p>{t("portfolio.brokerSync.operationKey")}: <code className="break-all">{safe(issue.operationKey)}</code></p>
          {issue.id && <p>ID записи: <code className="break-all">{safe(issue.id)}</code></p>}
          <p>{t("portfolio.brokerSync.issueDetected")}: {safe(formatDateTime(issue.lastDetectedAt))}</p>
          <p className="whitespace-pre-wrap">{safe(issue.text)}</p>
          <pre className="max-h-64 w-full min-w-0 max-w-full overflow-auto whitespace-pre-wrap break-all rounded-md bg-muted p-3">{JSON.stringify(safeBrokerReviewValue(issue.payload, secrets), null, 2)}</pre>
        </div>
      </details>
    </article>
  );
}
